// PDF export service - Cahier des charges Enseignes3D.fr
// Two exports: Client quote + Workshop production sheet
import { jsPDF } from 'jspdf';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_neon-builder-pro/artifacts/5t0ljgml_Enseignes3D-blanc-1024x199.png';
const ACCENT = '#ec1d8c';
const DARK = '#1a1a20';
const TEXT = '#333333';

function rgbHex(hex) {
  if (typeof hex !== 'string' || !hex) return [0, 0, 0];
  const h = hex.replace('#', '');
  if (h.length < 6) return [0, 0, 0];
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return [isNaN(r) ? 0 : r, isNaN(g) ? 0 : g, isNaN(b) ? 0 : b];
}
function setColor(doc, fn, hex) { const [r, g, b] = rgbHex(hex); doc[fn](r, g, b); }

function captureCanvas() {
  const canvases = document.querySelectorAll('.editor-canvas canvas');
  let main = null; let maxArea = 0;
  canvases.forEach((c) => { const a = c.width * c.height; if (a > maxArea) { maxArea = a; main = c; } });
  if (!main) return null;
  try { return main.toDataURL('image/jpeg', 0.92); } catch { return null; }
}

async function loadLogoBase64() {
  try {
    const resp = await fetch(LOGO_URL);
    const blob = await resp.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (e) { return null; }
}

function computeQuote(state) {
  const { segments, dimensions, structureType, pricing, ledOn, ledDensity, standoffs } = state;
  const charCount = segments.reduce((a, s) => a + (s.text?.trim().length || 0), 0);
  const surfacePerCharCm2 = (dimensions.height * dimensions.height * 0.55) / 100;
  const depthCm = dimensions.depth / 10;
  const acrThkCm = dimensions.acrylicThickness / 10;
  const backThkCm = 0.3;

  let petgVolCm3 = 0, pmmaSurfaceCm2 = 0, pvcSurfaceCm2 = 0, woodSurfaceCm2 = 0;
  const bom = []; // bill of materials per segment

  segments.forEach((seg) => {
    const cc = (seg.text || '').trim().length;
    if (!cc) return;
    const segSurf = surfacePerCharCm2 * cc;
    const partsCfg = [
      { name: 'Face avant', key: 'front', mat: seg.materials?.front, factor: acrThkCm, surf: segSurf },
      { name: 'Parois',     key: 'walls', mat: seg.materials?.walls, factor: depthCm * 0.18, surf: segSurf * 0.6 },
      { name: 'Fond',       key: 'back',  mat: seg.materials?.back,  factor: backThkCm, surf: segSurf },
    ];
    partsCfg.forEach((p) => {
      const vol = segSurf * p.factor;
      if (p.mat === 'petg') petgVolCm3 += vol;
      else if (p.mat === 'pmma') pmmaSurfaceCm2 += p.surf;
      else if (p.mat === 'pvc')  pvcSurfaceCm2 += p.surf;
      else if (p.mat === 'wood') woodSurfaceCm2 += p.surf;
      bom.push({ segment: seg.text, ...p, vol, color: seg.colors[p.key] });
    });
  });

  const petgKg = (petgVolCm3 * 1.27) / 1000;
  const FILSEC = Math.PI * 0.0875 * 0.0875;
  const petgFilamentM = (petgVolCm3 / FILSEC) / 100;
  const petgCost = petgKg * pricing.petgPerKg;
  const pmmaCost = pmmaSurfaceCm2 * pricing.pmmaPerCm2;
  const pvcCost  = pvcSurfaceCm2  * pricing.pvcPerCm2;
  const woodCost = woodSurfaceCm2 * pricing.woodPerCm2;
  const materialsCost = petgCost + pmmaCost + pvcCost + woodCost;

  const hPerEl = ledOn ? (pricing.laborHoursPerElementLit ?? 1) : (pricing.laborHoursPerElementNoLit ?? 0.5);
  const laborHours = segments.length * hPerEl;
  const laborCost = laborHours * pricing.laborPerHour;

  const purchaseCost = materialsCost + laborCost;
  const marginAmount = purchaseCost * (pricing.marginPct / 100);
  const totalHT = purchaseCost + marginAmount;
  const totalTTC = totalHT * 1.2;

  // LED
  const charArea = dimensions.height * dimensions.height * 0.55;
  const ledLength = ledOn ? +((charArea * charCount) / 30000).toFixed(2) : 0;
  const ledCount  = ledOn ? Math.ceil(ledLength * ledDensity) : 0;

  // Clips
  const totalClips = segments.length * 4;

  return {
    segments, dimensions, structureType, ledOn, standoffs,
    bom,
    petgKg: +petgKg.toFixed(3), petgFilamentM: +petgFilamentM.toFixed(1), petgVolCm3: +petgVolCm3.toFixed(1), petgCost: +petgCost.toFixed(2),
    pmmaSurfaceCm2: +pmmaSurfaceCm2.toFixed(1), pmmaCost: +pmmaCost.toFixed(2),
    pvcSurfaceCm2: +pvcSurfaceCm2.toFixed(1), pvcCost: +pvcCost.toFixed(2),
    woodSurfaceCm2: +woodSurfaceCm2.toFixed(1), woodCost: +woodCost.toFixed(2),
    materialsCost: +materialsCost.toFixed(2),
    laborHours: +laborHours.toFixed(2), laborCost: +laborCost.toFixed(2),
    purchaseCost: +purchaseCost.toFixed(2),
    marginPct: pricing.marginPct, marginAmount: +marginAmount.toFixed(2),
    totalHT: +totalHT.toFixed(2), totalTTC: +totalTTC.toFixed(2),
    ledLength, ledCount, ledDensity,
    totalClips,
    pricing,
  };
}

async function makeHeader(doc, title, subtitle, logoData) {
  const W = 210;
  setColor(doc, 'setFillColor', DARK);
  doc.rect(0, 0, W, 30, 'F');
  if (logoData) {
    try { doc.addImage(logoData, 'PNG', 14, 7, 50, 9.7, undefined, 'FAST'); }
    catch (e) { /* fallback text */ }
  }
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  const now = new Date();
  doc.text(now.toLocaleDateString('fr-FR') + ' ' + now.toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' }), W - 14, 12, { align: 'right' });
  setColor(doc, 'setTextColor', ACCENT);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(title, W - 14, 19, { align: 'right' });
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(subtitle, W - 14, 25, { align: 'right' });
}

function makeFooter(doc, label) {
  const W = 210, H = 297;
  setColor(doc, 'setTextColor', '#999999');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text(`Enseignes3D.fr \u2014 ${label}`, W / 2, H - 8, { align: 'center' });
}

// ===== EXPORT CLIENT =====
export async function exportClientPDF(state) {
  const q = computeQuote(state);
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = 210, pad = 14;
  let y = 38;
  const logo = await loadLogoBase64();
  await makeHeader(doc, 'DEVIS CLIENT', 'Devis indicatif pour validation', logo);

  const name = state.meta?.name || state.segments.map((s) => s.text).join('') || 'Projet';
  setColor(doc, 'setTextColor', DARK);
  doc.setFontSize(16); doc.setFont('helvetica', 'bold');
  doc.text(`${name}`, pad, y);
  y += 6;

  // Client + install block
  const meta = state.meta || {};
  if (meta.client || meta.installAddress || meta.reference) {
    setColor(doc, 'setTextColor', TEXT);
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    if (meta.reference) { doc.text(`R\u00e9f. : ${meta.reference}`, pad, y); y += 4; }
    if (meta.client) { doc.text(`Client : ${meta.client}`, pad, y); y += 4; }
    if (meta.installAddress) {
      const wrapped = doc.splitTextToSize(`Installation : ${meta.installAddress}`, W - pad * 2);
      doc.text(wrapped, pad, y); y += 4 * wrapped.length;
    }
    if (meta.contactEmail || meta.contactPhone) {
      const c = [meta.contactEmail, meta.contactPhone].filter(Boolean).join('  \u2022  ');
      doc.text(`Contact : ${c}`, pad, y); y += 4;
    }
    if (meta.deadlineDate) {
      const d = new Date(meta.deadlineDate);
      if (!isNaN(d)) { doc.text(`\u00c9ch\u00e9ance : ${d.toLocaleDateString('fr-FR')}`, pad, y); y += 4; }
    }
    y += 2;
  }

  const img = captureCanvas();
  if (img) { doc.addImage(img, 'JPEG', pad, y, W - pad * 2, 90, undefined, 'FAST'); y += 96; }

  // Description (résumé visuel)
  setColor(doc, 'setFillColor', '#f5f5f5');
  doc.rect(pad, y, W - pad * 2, 24, 'F');
  setColor(doc, 'setTextColor', ACCENT);
  doc.setFontSize(11); doc.setFont('helvetica', 'bold');
  doc.text('DESCRIPTION DU PROJET', pad + 3, y + 7);
  setColor(doc, 'setTextColor', TEXT);
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text(`Texte : ${name}  \u00b7  ${state.segments.length} segment(s)`, pad + 3, y + 13);
  doc.text(`Dimensions : H ${state.dimensions.height} mm  \u00b7  P ${state.dimensions.depth} mm`, pad + 3, y + 18);
  if (q.ledOn) doc.text(`\u00c9clairage LED inclus : ${q.ledLength} m de ruban (${q.ledCount} LEDs)`, pad + 3, y + 23);
  y += 30;

  // PRIX (client view: only total HT + TTC, no breakdown of margin)
  setColor(doc, 'setFillColor', '#fff0f8');
  doc.rect(pad, y, W - pad * 2, 36, 'F');
  setColor(doc, 'setTextColor', ACCENT);
  doc.setFontSize(14); doc.setFont('helvetica', 'bold');
  doc.text('PRIX FORFAITAIRE', pad + 3, y + 9);
  setColor(doc, 'setTextColor', TEXT);
  doc.setFontSize(11); doc.setFont('helvetica', 'normal');
  doc.text(`Total HT`, pad + 3, y + 19);
  setColor(doc, 'setTextColor', DARK);
  doc.setFont('helvetica', 'bold');
  doc.text(`${q.totalHT} \u20ac`, W - pad - 3, y + 19, { align: 'right' });
  setColor(doc, 'setTextColor', TEXT);
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text(`TVA 20%`, pad + 3, y + 26);
  doc.text(`${(q.totalTTC - q.totalHT).toFixed(2)} \u20ac`, W - pad - 3, y + 26, { align: 'right' });
  setColor(doc, 'setTextColor', ACCENT);
  doc.setFontSize(14); doc.setFont('helvetica', 'bold');
  doc.text(`Total TTC`, pad + 3, y + 33);
  doc.text(`${q.totalTTC} \u20ac`, W - pad - 3, y + 33, { align: 'right' });
  y += 42;

  // Conditions
  setColor(doc, 'setTextColor', TEXT);
  doc.setFontSize(8); doc.setFont('helvetica', 'italic');
  doc.text(`Ce devis comprend : conception 3D, fabrication, finitions, assemblage et contr\u00f4le qualit\u00e9.`, pad, y);
  y += 4;
  doc.text(`Livraison sous 7 \u00e0 10 jours ouvr\u00e9s apr\u00e8s validation. Pose et installation non incluses.`, pad, y);
  y += 4;
  doc.text(`Devis valable 30 jours \u2014 acompte 40% \u00e0 la commande, solde \u00e0 la livraison.`, pad, y);

  makeFooter(doc, 'Devis client \u2014 Document confidentiel');
  const filename = `devis_client_${name.replace(/[^a-z0-9]/gi, '_')}_${Date.now()}.pdf`;
  doc.save(filename);
  return filename;
}

// ===== EXPORT ATELIER =====
export async function exportWorkshopPDF(state) {
  const q = computeQuote(state);
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = 210, pad = 14;
  let y = 38;
  const logo = await loadLogoBase64();
  await makeHeader(doc, 'BON D\'ATELIER', 'Fiche de production interne', logo);

  const name = state.segments.map((s) => s.text).join('') || 'Projet';
  setColor(doc, 'setTextColor', DARK);
  doc.setFontSize(14); doc.setFont('helvetica', 'bold');
  doc.text(`Projet : ${name}`, pad, y);
  y += 7;

  const img = captureCanvas();
  if (img) { doc.addImage(img, 'JPEG', pad, y, W - pad * 2, 60, undefined, 'FAST'); y += 64; }

  // Dimensions / structure
  setColor(doc, 'setFillColor', '#f5f5f5');
  doc.rect(pad, y, W - pad * 2, 14, 'F');
  setColor(doc, 'setTextColor', ACCENT);
  doc.setFontSize(10); doc.setFont('helvetica', 'bold');
  doc.text('SP\u00c9CIFICATIONS', pad + 3, y + 6);
  setColor(doc, 'setTextColor', TEXT);
  doc.setFontSize(8); doc.setFont('helvetica', 'normal');
  doc.text(`H ${state.dimensions.height} mm \u00b7 P ${state.dimensions.depth} mm \u00b7 paroi ${state.dimensions.wallThickness} mm \u00b7 PMMA ${state.dimensions.acrylicThickness} mm \u00b7 structure ${state.structureType}`, pad + 3, y + 11);
  y += 18;

  // Nomenclature pièces
  setColor(doc, 'setTextColor', ACCENT);
  doc.setFontSize(10); doc.setFont('helvetica', 'bold');
  doc.text('NOMENCLATURE DES PI\u00c8CES', pad, y);
  y += 4;
  setColor(doc, 'setFontSize', 8);
  doc.setFontSize(8); doc.setFont('helvetica', 'bold');
  setColor(doc, 'setTextColor', '#666666');
  doc.text('Segment', pad, y);
  doc.text('Pi\u00e8ce', pad + 35, y);
  doc.text('Mat.', pad + 65, y);
  doc.text('Couleur', pad + 85, y);
  doc.text('Surf/Vol', pad + 115, y);
  doc.text('Co\u00fbt achat', W - pad, y, { align: 'right' });
  y += 2;
  setColor(doc, 'setDrawColor', '#cccccc');
  doc.line(pad, y, W - pad, y);
  y += 4;
  setColor(doc, 'setTextColor', TEXT);
  doc.setFont('helvetica', 'normal');
  const matLabels = { pmma: 'PMMA', petg: 'PETG', pvc: 'PVC', wood: 'Bois' };
  q.bom.forEach((b) => {
    const unit = b.mat === 'petg' ? `${(b.vol * 1.27).toFixed(1)} g` : `${b.surf.toFixed(1)} cm\u00b2`;
    const cost = b.mat === 'petg' ? ((b.vol * 1.27 / 1000) * q.pricing.petgPerKg)
              : b.mat === 'pmma' ? (b.surf * q.pricing.pmmaPerCm2)
              : b.mat === 'pvc'  ? (b.surf * q.pricing.pvcPerCm2)
              : b.mat === 'wood' ? (b.surf * q.pricing.woodPerCm2) : 0;
    doc.text(b.segment, pad, y);
    doc.text(b.name, pad + 35, y);
    doc.text(matLabels[b.mat] || '-', pad + 65, y);
    const [r, g, bl] = rgbHex(b.color);
    doc.setFillColor(r, g, bl);
    doc.rect(pad + 85, y - 3, 5, 4, 'F');
    doc.text(b.color, pad + 92, y);
    doc.text(unit, pad + 115, y);
    doc.text(`${cost.toFixed(2)} \u20ac`, W - pad, y, { align: 'right' });
    y += 5;
  });
  y += 3;

  // Synthèse fournitures
  setColor(doc, 'setFillColor', '#f0f8ff');
  doc.rect(pad, y, W - pad * 2, 38, 'F');
  setColor(doc, 'setTextColor', ACCENT);
  doc.setFontSize(10); doc.setFont('helvetica', 'bold');
  doc.text('FOURNITURES TOTALES', pad + 3, y + 6);
  setColor(doc, 'setTextColor', TEXT);
  doc.setFontSize(8); doc.setFont('helvetica', 'normal');
  doc.text(`PETG : ${q.petgKg} kg  \u00b7  filament Ø1.75 : ${q.petgFilamentM} m`, pad + 3, y + 12);
  doc.text(`PMMA : ${q.pmmaSurfaceCm2} cm\u00b2 \u00b7 PVC : ${q.pvcSurfaceCm2} cm\u00b2 \u00b7 Bois : ${q.woodSurfaceCm2} cm\u00b2`, pad + 3, y + 17);
  doc.text(`Clips d'assemblage : ${q.totalClips} (ABS, 4 par segment)`, pad + 3, y + 22);
  if (q.ledOn) doc.text(`Ruban LED : ${q.ledLength} m \u00b7 ${q.ledCount} LEDs \u00b7 ${q.ledDensity} LED/m`, pad + 3, y + 27);
  if (state.standoffs?.enabled) doc.text(`Entretoises : 4 \u00d7 \u00d8${state.standoffs.diameter} mm \u00b7 h ${state.standoffs.height} mm \u00b7 alu`, pad + 3, y + 32);
  y += 42;

  // Coûts détaillés
  setColor(doc, 'setTextColor', ACCENT);
  doc.setFontSize(10); doc.setFont('helvetica', 'bold');
  doc.text('CO\u00dbTS DE FABRICATION', pad, y);
  y += 5;
  setColor(doc, 'setDrawColor', '#cccccc');
  doc.line(pad, y, W - pad, y);
  y += 4;
  setColor(doc, 'setTextColor', TEXT);
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  const lines = [
    [`PETG (${q.petgKg} kg \u00d7 ${q.pricing.petgPerKg}€/kg)`, `${q.petgCost} \u20ac`],
    [`PMMA (${q.pmmaSurfaceCm2} cm\u00b2 \u00d7 ${q.pricing.pmmaPerCm2}€/cm\u00b2)`, `${q.pmmaCost} \u20ac`],
    [`PVC (${q.pvcSurfaceCm2} cm\u00b2 \u00d7 ${q.pricing.pvcPerCm2}€/cm\u00b2)`, `${q.pvcCost} \u20ac`],
    [`Bois (${q.woodSurfaceCm2} cm\u00b2 \u00d7 ${q.pricing.woodPerCm2}€/cm\u00b2)`, `${q.woodCost} \u20ac`],
    [`Main d'\u0153uvre (${q.laborHours} h \u00d7 ${q.pricing.laborPerHour}€/h)`, `${q.laborCost} \u20ac`],
  ];
  lines.forEach(([l, v]) => { doc.text(l, pad, y); doc.text(v, W - pad, y, { align: 'right' }); y += 5; });
  doc.line(pad, y, W - pad, y); y += 5;
  doc.setFont('helvetica', 'bold');
  doc.text(`Co\u00fbt d'achat total`, pad, y);
  doc.text(`${q.purchaseCost} \u20ac`, W - pad, y, { align: 'right' });
  y += 6;
  setColor(doc, 'setTextColor', '#16a34a');
  doc.text(`Marge ${q.marginPct}%`, pad, y);
  doc.text(`+ ${q.marginAmount} \u20ac`, W - pad, y, { align: 'right' });
  y += 6;
  setColor(doc, 'setTextColor', ACCENT);
  doc.setFontSize(11);
  doc.text(`PRIX DE VENTE HT`, pad, y);
  doc.text(`${q.totalHT} \u20ac`, W - pad, y, { align: 'right' });
  y += 6;
  setColor(doc, 'setTextColor', TEXT);
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text(`Prix vente TTC (TVA 20%)`, pad, y);
  doc.text(`${q.totalTTC} \u20ac`, W - pad, y, { align: 'right' });

  makeFooter(doc, 'Bon d\'atelier \u2014 INTERNE \u2014 Ne pas remettre au client');

  // ===== PAGE : PLAN DE PERÇAGE MURAL =====
  drawDrillingPlan(doc, state);

  const filename = `bon_atelier_${name.replace(/[^a-z0-9]/gi, '_')}_${Date.now()}.pdf`;
  doc.save(filename);
  return filename;
}

// ============================================================================
// Wall drilling plan (page ajoutée au PDF atelier)
// ============================================================================
function drawDrillingPlan(doc, state) {
  const { segments, dimensions, standoffs } = state;
  if (!standoffs?.enabled) return;

  doc.addPage('a4', 'landscape');
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const pad = 15;
  let y = pad;

  // Header
  setColor(doc, 'setFillColor', DARK);
  doc.rect(0, 0, W, 22, 'F');
  setColor(doc, 'setTextColor', '#ffffff');
  doc.setFontSize(14); doc.setFont('helvetica', 'bold');
  doc.text('PLAN DE PERÇAGE MURAL — À L\'ÉCHELLE', pad, 14);
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text(`Vis : ${Math.max(2, standoffs.count || 2)} par élément — Ø${standoffs.diameter} mm — ${standoffs.material === 'aluminum' ? 'aluminium' : 'inox'}`, W - pad, 14, { align: 'right' });
  y = 30;

  // Estimate total width (mm) of the sign
  const heightMm = dimensions.height;
  const totalWidthMm = segments.reduce((sum, s) => {
    const chars = (s.text || '').length;
    return sum + chars * heightMm * 0.6 + (s.detached ? (s.gapAfterMm || 30) : 15);
  }, 0);

  // Compute scale (fit within landscape A4 usable area)
  const usableW = W - pad * 2;
  const usableH = H - y - 40;
  const scale = Math.min(usableW / (totalWidthMm + 40), usableH / (heightMm + 40));
  const drawW = totalWidthMm * scale;
  const drawH = heightMm * scale;
  const originX = (W - drawW) / 2;
  const originY = y + 20;

  // Draw scale info
  setColor(doc, 'setTextColor', TEXT);
  doc.setFontSize(9);
  doc.text(`Échelle 1:${Math.round(1 / scale)}   |   Largeur ${Math.round(totalWidthMm)} mm × Hauteur ${heightMm} mm`, pad, y + 6);

  // Bounding box (dashed) for the sign envelope
  setColor(doc, 'setDrawColor', '#a0a0a8');
  doc.setLineDashPattern([1.5, 1.5], 0);
  doc.setLineWidth(0.3);
  doc.rect(originX, originY, drawW, drawH, 'S');

  // Draw each segment's outline + drilling markers
  let cursorMm = 0;
  segments.forEach((seg, si) => {
    const chars = (seg.text || '').length;
    const segWmm = chars * heightMm * 0.6;
    const segX = originX + cursorMm * scale;
    const segY = originY;
    const segW = segWmm * scale;
    // Segment outline
    setColor(doc, 'setDrawColor', si === 0 ? ACCENT : '#3b82f6');
    doc.setLineDashPattern([], 0);
    doc.setLineWidth(0.6);
    doc.rect(segX, segY, segW, drawH, 'S');
    // Segment label
    setColor(doc, 'setTextColor', si === 0 ? ACCENT : '#3b82f6');
    doc.setFontSize(9); doc.setFont('helvetica', 'bold');
    doc.text(seg.text || `Segment ${si + 1}`, segX + segW / 2, segY - 3, { align: 'center' });

    // Drilling markers
    const nStand = Math.max(2, standoffs.count || 2);
    const marginRatio = 0.12;
    const stX0 = segX + segW * marginRatio;
    const stX1 = segX + segW * (1 - marginRatio);
    const stY = segY + drawH * 0.85; // bottom of the letter
    setColor(doc, 'setFillColor', '#ec1d8c');
    setColor(doc, 'setDrawColor', '#ec1d8c');
    doc.setLineWidth(0.4);
    for (let i = 0; i < nStand; i++) {
      const t = nStand === 1 ? 0.5 : i / (nStand - 1);
      const sx = stX0 + (stX1 - stX0) * t;
      const r = Math.max(0.6, (standoffs.diameter / 2) * scale);
      // Draw cross-hair + circle
      doc.circle(sx, stY, r, 'FD');
      doc.line(sx - r * 2, stY, sx + r * 2, stY);
      doc.line(sx, stY - r * 2, sx, stY + r * 2);
      // Distance dimension label below
      setColor(doc, 'setTextColor', DARK);
      doc.setFontSize(7); doc.setFont('helvetica', 'normal');
      const distMm = Math.round(((sx - segX) / scale));
      doc.text(`${distMm} mm`, sx, stY + r * 3 + 3, { align: 'center' });
    }

    cursorMm += segWmm + (seg.detached ? (seg.gapAfterMm || 30) : 15);
  });

  // Legend + instructions
  y = originY + drawH + 20;
  setColor(doc, 'setTextColor', TEXT);
  doc.setFontSize(9); doc.setFont('helvetica', 'bold');
  doc.text('Procédure de pose :', pad, y); y += 5;
  doc.setFont('helvetica', 'normal');
  const steps = [
    `1. Reporter le plan à l'échelle 1:${Math.round(1 / scale)} sur le mur (utiliser un niveau à bulle).`,
    `2. Percer aux repères roses : Ø ${standoffs.diameter} mm, profondeur ${Math.round(standoffs.height * 1.5)} mm.`,
    `3. Insérer les chevilles adaptées (support mur : béton = M${Math.max(6, Math.round(standoffs.diameter * 0.5))}, placo = MolyBolt).`,
    `4. Visser les entretoises (${standoffs.material === 'aluminum' ? 'alu' : 'inox'} Ø${standoffs.diameter} mm h=${standoffs.height} mm).`,
    `5. Aligner les trous du fond du socle sur les entretoises et enclipser.`,
    `6. Vérifier la planéité et serrer.`,
  ];
  steps.forEach((s) => { doc.text(s, pad, y); y += 4; });

  makeFooter(doc, 'Plan de perçage mural \u2014 À reporter à l\'échelle indiquée sur le mur');
}

// Legacy default export (kept for compat) - generates client PDF
export const exportProjectPDF = exportClientPDF;
